import { useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Home, FileText, FlaskConical, Users, User, Wallet, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import ResqlyLiquidSurface from './ResqlyLiquidSurface';
import { springNav } from './motion';

const CONSUMER_TABS = [
  { key: 'home', label: 'Home', icon: Home, path: '/consumer/home' },
  { key: 'prescriptions', label: 'Rx', icon: FileText, path: '/consumer/prescriptions' },
  { key: 'lab', label: 'Labs', icon: FlaskConical, path: '/consumer/lab-tests' },
  { key: 'family', label: 'Family', icon: Users, path: '/consumer/family' },
  { key: 'profile', label: 'Profile', icon: User, path: '/consumer/profile' },
];

const PROVIDER_TABS = [
  { key: 'home', label: 'Home', icon: Home, path: '/provider/dashboard' },
  { key: 'orders', label: 'Orders', icon: ClipboardList, path: '/provider/orders' },
  { key: 'earnings', label: 'Earnings', icon: Wallet, path: '/provider/earnings' },
  { key: 'profile', label: 'Profile', icon: User, path: '/provider/profile' },
];

export default function ResqlyGlassNav({ variant = 'consumer' }) {
  const tabs = variant === 'provider' ? PROVIDER_TABS : CONSUMER_TABS;
  const navigate = useNavigate();
  const loc = useLocation();
  const reduceMotion = useReducedMotion();

  const activeIndex = tabs.findIndex((t) => loc.pathname.startsWith(t.path));
  const safeActive = activeIndex >= 0 ? activeIndex : 0;

  return (
    <nav
      className="sticky bottom-0 left-0 right-0 z-30 pointer-events-none"
      style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
      aria-label={variant === 'provider' ? 'Provider navigation' : 'Customer navigation'}
    >
      <div className="px-3 pt-2 pointer-events-auto mx-auto w-full max-w-[min(100%,480px)]">
        <ResqlyLiquidSurface
          className="rounded-[28px] px-1.5 py-1.5"
          filterId={`resqly-nav-glass-${variant}`}
        >
          <div className="relative flex items-stretch justify-around gap-0.5">
            {!reduceMotion && (
              <motion.div
                className="absolute top-1 bottom-1 rounded-[20px] bg-gradient-to-b from-blue-600 to-blue-700 shadow-[0_8px_20px_rgba(30,64,175,0.35)]"
                layoutId={`nav-active-${variant}`}
                transition={springNav}
                style={{
                  width: `calc(${100 / tabs.length}% - 4px)`,
                  left: `calc(${(safeActive * 100) / tabs.length}% + 2px)`,
                }}
              />
            )}
            {tabs.map((t, i) => {
              const active = loc.pathname.startsWith(t.path);
              const Icon = t.icon;
              return (
                <button
                  data-testid={`nav-${variant}-${t.key}`}
                  key={t.key}
                  type="button"
                  onClick={() => navigate(t.path)}
                  className={cn(
                    'relative z-10 flex flex-1 flex-col items-center justify-center gap-0.5 rounded-[20px] py-2 px-1 min-w-0',
                    'transition-colors duration-200 active:scale-95',
                    active ? 'text-white' : 'text-slate-600',
                    reduceMotion && active && 'bg-gradient-to-b from-blue-600 to-blue-700 shadow-md',
                  )}
                  aria-current={active ? 'page' : undefined}
                >
                  <motion.span
                    animate={active && !reduceMotion ? { scale: 1.08, y: -1 } : { scale: 1, y: 0 }}
                    transition={springNav}
                    className="flex items-center justify-center"
                  >
                    <Icon className="h-[19px] w-[19px]" strokeWidth={active ? 2.4 : 2} />
                  </motion.span>
                  <span
                    className={cn(
                      'text-[10px] font-semibold tracking-tight truncate max-w-full',
                      active ? 'opacity-100' : 'opacity-80',
                    )}
                  >
                    {t.label}
                  </span>
                </button>
              );
            })}
          </div>
        </ResqlyLiquidSurface>
      </div>
    </nav>
  );
}
